import type { SendFailure } from '../engine/send-failure';
/**
 * WhatsApp Tools Commands Interface
 *
 * This file defines all WhatsApp tool command names as constants.
 * It serves as the single source of truth for command names used by both:
 * - Client (extension): wa-tools-client-actions.ts
 * - Server (notifier): wa-tools-notifier-handlers.ts, NotiferHandlers.ts
 *
 * IMPORTANT: When adding a new WhatsApp tool command:
 * 1. Add the command constant here
 * 2. Update wa-tools-client-actions.ts to use the constant
 * 3. Update wa-tools-notifier-handlers.ts to handle the command
 */

// ============================================================================
// WhatsApp Tool Command Constants
// ============================================================================

const WA_TOOLS_PREFIX = 'whatsappTools.'
export const WA_TOOL_COMMANDS = {
    // ── Validation Domain ───────────────────────────────────────────────────
    waValidatePhoneNumberOrChatIdTool: `${WA_TOOLS_PREFIX}waValidatePhoneNumberOrChatIdTool`,
    waGetLinkPreviewTool: `${WA_TOOLS_PREFIX}waGetLinkPreviewTool`,

    // ── Chat Domain ──────────────────────────────────────────────────────
    waGetProfilePicUrlTool: `${WA_TOOLS_PREFIX}waGetProfilePicUrlTool`,
    waGetChatObjectByChatIdTool: `${WA_TOOLS_PREFIX}waGetChatObjectByChatIdTool`,
    waGetChatParticipantsTool: `${WA_TOOLS_PREFIX}waGetChatParticipantsTool`,
    waQueryContactsAndChatsTool: `${WA_TOOLS_PREFIX}waQueryContactsAndChatsTool`,
    waGetLatestChatsAndMessagesTool: `${WA_TOOLS_PREFIX}waGetLatestChatsAndMessagesTool`,
    /** @internal — extension-only, reads the client-side IndexedDB message-vault
     *  (all chats, cursor-paged). Vault lives on the web.whatsapp.com origin, so
     *  this only runs in the engine/page context; the website reaches it via /actions. */
    waQueryVaultMessagesTool: `${WA_TOOLS_PREFIX}waQueryVaultMessagesTool`,
    waSendSeenTool: `${WA_TOOLS_PREFIX}waSendSeenTool`,
    waOpenChatTool: `${WA_TOOLS_PREFIX}waOpenChatTool`,
    waClearMessagesTool: `${WA_TOOLS_PREFIX}waClearMessagesTool`,
    waDeleteChatTool: `${WA_TOOLS_PREFIX}waDeleteChatTool`,
    waArchiveChatTool: `${WA_TOOLS_PREFIX}waArchiveChatTool`,
    waUnarchiveChatTool: `${WA_TOOLS_PREFIX}waUnarchiveChatTool`,
    waPinChatTool: `${WA_TOOLS_PREFIX}waPinChatTool`,
    waUnpinChatTool: `${WA_TOOLS_PREFIX}waUnpinChatTool`,
    waMuteChatTool: `${WA_TOOLS_PREFIX}waMuteChatTool`,
    waUnmuteChatTool: `${WA_TOOLS_PREFIX}waUnmuteChatTool`,
    waMarkChatUnreadTool: `${WA_TOOLS_PREFIX}waMarkChatUnreadTool`,
    /** @internal — extension-only, no v1 exposure (DOM-coupled typing indicator) */
    waSendStateTypingTool: `${WA_TOOLS_PREFIX}waSendStateTypingTool`,
    /** @internal — extension-only, no v1 exposure (DOM-coupled recording indicator) */
    waSendStateRecordingTool: `${WA_TOOLS_PREFIX}waSendStateRecordingTool`,
    /** @internal — extension-only, no v1 exposure (DOM-coupled typing/recording clear) */
    waClearStateTool: `${WA_TOOLS_PREFIX}waClearStateTool`,
    waGetChatLabelsTool: `${WA_TOOLS_PREFIX}waGetChatLabelsTool`,
    waChangeLabelsTool: `${WA_TOOLS_PREFIX}waChangeLabelsTool`,
    /** List ALL WhatsApp Business labels defined on the connected account
     *  (wraps the wwebjs fork `Client.getLabels()`). Read-only; WhatsApp
     *  Business accounts only. Engine handler lives in the extension repo. */
    waGetLabelsTool: `${WA_TOOLS_PREFIX}waGetLabelsTool`,
    waGetPinnedMessagesTool: `${WA_TOOLS_PREFIX}waGetPinnedMessagesTool`,
    waAddOrEditCustomerNoteTool: `${WA_TOOLS_PREFIX}waAddOrEditCustomerNoteTool`,
    // ── Group Domain ──────────────────────────────────────────────────────
    waCreateGroupTool: `${WA_TOOLS_PREFIX}waCreateGroupTool`,
    waUpdateGroupNameTool: `${WA_TOOLS_PREFIX}waUpdateGroupNameTool`,
    waGetGroupDataTool: `${WA_TOOLS_PREFIX}waGetGroupDataTool`,
    waUpdateGroupDescriptionTool: `${WA_TOOLS_PREFIX}waUpdateGroupDescriptionTool`,
    waAddGroupParticipantTool: `${WA_TOOLS_PREFIX}waAddGroupParticipantTool`,
    waRemoveGroupParticipantTool: `${WA_TOOLS_PREFIX}waRemoveGroupParticipantTool`,
    waSetGroupAdminRightsTool: `${WA_TOOLS_PREFIX}waSetGroupAdminRightsTool`,
    waRemoveGroupAdminRightsTool: `${WA_TOOLS_PREFIX}waRemoveGroupAdminRightsTool`,
    waSetGroupPictureTool: `${WA_TOOLS_PREFIX}waSetGroupPictureTool`,
    waLeaveGroupTool: `${WA_TOOLS_PREFIX}waLeaveGroupTool`,
    /** @internal — extension-only, no v1 exposure (WAWeb common-groups store query) */
    waGetCommonGroupsTool: `${WA_TOOLS_PREFIX}waGetCommonGroupsTool`,
    /** @internal — extension-only, no v1 exposure (JID format helper) */
    CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED: `${WA_TOOLS_PREFIX}convertWaLidSerializedToCusSerialized`,
    /** @internal — no v1 exposure. Reverse of the above: resolve a phone
     *  (`<digits>@c.us`) to its user-level `<lid>@lid` via the engine's pn→lid
     *  mapping. Baileys-only today (BE#676 lazy @lid rekey); returns a NULLABLE
     *  lid so "no mapping" is distinguishable from a transient engine error. */
    CONVERT_WA_CUS_SERIALIZED_TO_WA_LID_SERIALIZED: `${WA_TOOLS_PREFIX}convertWaCusSerializedToWaLidSerialized`,

    // ── Contact Domain ───────────────────────────────────────────────────

    // ── Newsletter (Channel) Domain ───────────────────────────────────────
    waCreateNewsletterTool: `${WA_TOOLS_PREFIX}waCreateNewsletterTool`,
    waGetNewsletterTool: `${WA_TOOLS_PREFIX}waGetNewsletterTool`,

    // ── Message Domain ──────────────────────────────────────────────────────
    sendMessageThunkTool: `${WA_TOOLS_PREFIX}sendMessageThunkTool`,
    waQueryMessagesTool: `${WA_TOOLS_PREFIX}waQueryMessagesTool`,
    waLoadMessagesFromPhoneTool: `${WA_TOOLS_PREFIX}waLoadMessagesFromPhoneTool`,
    waGetMessageByKeyTool: `${WA_TOOLS_PREFIX}waGetMessageByKeyTool`,
    /** @internal — extension-only, no v1 exposure (phone-history primitive, distinct from public `load_older`) */
    /** @internal — extension-only, no v1 exposure (message-store lookup helper) */
    GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE: `${WA_TOOLS_PREFIX}getUnloggedWaMessageKeyIfExistInStore`,
    /** Cross-engine scheduling dispatch — pod-side handler in
     *  src/gateway/baileys/handlers/scheduling-protocol.ts. Body shape:
     *  bt-common's InsertOnlyIfMissingRequest.
     *  @internal — server-internal idempotency, no v1 exposure */
    insertOnlyIfMissing: `${WA_TOOLS_PREFIX}insertOnlyIfMissing`,
    waReactToMessageThunkTool: `${WA_TOOLS_PREFIX}waReactToMessageThunkTool`,
    /** Forward an existing message (by key) to one or more chats. */
    waForwardMessageTool: `${WA_TOOLS_PREFIX}waForwardMessageTool`,
    /** Star or unstar an existing message (by key). */
    waStarMessageTool: `${WA_TOOLS_PREFIX}waStarMessageTool`,
    /** Delete an existing message (by key) — for me, or for everyone (revoke). */
    waDeleteMessageTool: `${WA_TOOLS_PREFIX}waDeleteMessageTool`,
    waPinMessageTool: `${WA_TOOLS_PREFIX}waPinMessageTool`,
    waUnpinMessageTool: `${WA_TOOLS_PREFIX}waUnpinMessageTool`,
    waGetMessageAckTool: `${WA_TOOLS_PREFIX}waGetMessageAckTool`,
    /** Read a poll's current votes: who voted for which option (read-only). */
    waGetPollResultsTool: `${WA_TOOLS_PREFIX}waGetPollResultsTool`,
    waGetMessagesAckDataTool: `${WA_TOOLS_PREFIX}waGetMessagesAckDataTool`,

    // ── Status Domain ──────────────────────────────────────────────────────
    createStatusTool: `${WA_TOOLS_PREFIX}createStatusTool`,

    // ── Media Domain ──────────────────────────────────────────────────────
    waGetMessageMediaTool: `${WA_TOOLS_PREFIX}waGetMessageMediaTool`,

    // ── Session Domain ──────────────────────────────────────────────────────
    getWaMeInfoTool: `${WA_TOOLS_PREFIX}getWaMeInfoTool`,
    isWhatsAppConnectedThunkTool: `${WA_TOOLS_PREFIX}isWhatsAppConnectedThunkTool`,
    /** @internal — extension-only, no v1 exposure (WAWeb store sanity check) */
    waValidateStoresTool: `${WA_TOOLS_PREFIX}waValidateStoresTool`,
    waReloadPageTool: `${WA_TOOLS_PREFIX}waReloadPageTool`,
    /** #1001 — extension-only: reload the WhatsApp Web tab unless the user is using it (notifier/guarded-reload.ts). */
    waGuardedReloadTool: `${WA_TOOLS_PREFIX}waGuardedReloadTool`,
    waTriggerQRTimeoutTool: `${WA_TOOLS_PREFIX}waTriggerQRTimeoutTool`,
    waTriggerLogoutTool: `${WA_TOOLS_PREFIX}waTriggerLogoutTool`,

    // ── Account Health Domain ──────────────────────────────────────────────
    // WhatsApp's own account-restriction ("reachout timelock") state plus the
    // new-chat message quota that PRECEDES a restriction. Both are read from WA
    // Web's user-prefs cache in the page context; the Baileys pod reads the same
    // two signals over MEX (identical GraphQL doc ids — 23983697327930364 and
    // 24503548349331633), so the two engines report the same thing.
    waAccountRestrictionTool: `${WA_TOOLS_PREFIX}waAccountRestrictionTool`,
    // Per-recipient pre-send risk. Mirrors WA's own canSendMsgWhileTimelocked:
    // while restricted, a send to an ordinary contact lands only if that chat
    // carries a trusted-contact token.
    waChatSendRiskTool: `${WA_TOOLS_PREFIX}waChatSendRiskTool`,

    // ── Labels Domain ──────────────────────────────────────────────────────
    /** @internal — extension-only, no v1 exposure (label/list UI ordering) */
    waUpdateShouldAppearChatsByListTool: `${WA_TOOLS_PREFIX}waUpdateShouldAppearChatsByListTool`,

    // ── Utility Domain ──────────────────────────────────────────────────────
    PING: `${WA_TOOLS_PREFIX}ping`,
    // Returns the engine's resolved timezone + current time. The engine runs
    // in the user's browser (extension) or a headless gateway pod (gateway),
    // so its `Intl.DateTimeFormat().resolvedOptions().timeZone` is the
    // authoritative user timezone. The MCP backend uses this to convert
    // localDueDateParts → UTC dueDate without falling back to the server's
    // host tz (UTC on staging) — see #engine-tz fix.
    currentDateTimeTool: `${WA_TOOLS_PREFIX}currentDateTimeTool`,
} as const;

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Union type of all WhatsApp tool command strings
 */
export type WaToolCommand = typeof WA_TOOL_COMMANDS[keyof typeof WA_TOOL_COMMANDS];

/**
 * Tools that are extension-internal / server-internal / DOM-coupled and
 * MUST NOT be exposed on the public /v1 surface. The diagnostic panel
 * uses this set to gray those rows; the v1 contract excludes them.
 *
 * Audited #159 — keep in sync with the `@internal` JSDoc tags above.
 */
export const INTERNAL_TOOLS: ReadonlySet<WaToolCommand> = new Set([
    WA_TOOL_COMMANDS.waSendStateTypingTool,
    WA_TOOL_COMMANDS.waSendStateRecordingTool,
    WA_TOOL_COMMANDS.waClearStateTool,
    WA_TOOL_COMMANDS.waValidateStoresTool,
    WA_TOOL_COMMANDS.waUpdateShouldAppearChatsByListTool,
    WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED,
    WA_TOOL_COMMANDS.CONVERT_WA_CUS_SERIALIZED_TO_WA_LID_SERIALIZED,
    WA_TOOL_COMMANDS.insertOnlyIfMissing,
    WA_TOOL_COMMANDS.GET_UNLOGGED_WA_MESSAGE_KEY_IF_EXIST_IN_STORE,
    WA_TOOL_COMMANDS.waQueryVaultMessagesTool,
]);

/**
 * Extension-internal IPC commands that are NOT wa-tools (no contract entry,
 * no v1 surface, no MQTT exposure). Used for content-script ↔ page bridge
 * primitives where the page is a pure data extractor and orchestration
 * lives in content-script (e.g. media fetch — page returns raw File,
 * content-script handles upload + extraction + BtSlim projection).
 *
 * Distinct namespace prefix (`__bt:internal/`) so they can't collide with
 * a future wa-tool string.
 */
export const EXTENSION_INTERNAL_COMMANDS = {
    btMediaPrimitive: '__bt:internal/mediaPrimitive',
    // On-demand vault sweep (backup capture-side gap closure). Content
    // triggers it before a backup run so the vault holds everything WA
    // surfaced while we were alive. NOT a WA_TOOL_COMMANDS contract entry —
    // purely extension-internal page IPC.
    btVaultSweep: '__bt:internal/vaultSweep',
    // Backup media phase: download a message's FULL media BYTES (base64) from
    // WhatsApp so the uploader can push them to the backup bucket. Returns raw
    // bytes (no upload) or a `skipped`/`error` marker. Extension-internal page IPC.
    btBackupMediaDownload: '__bt:internal/backupMediaDownload',
} as const;
export type ExtensionInternalCommand = (typeof EXTENSION_INTERNAL_COMMANDS)[keyof typeof EXTENSION_INTERNAL_COMMANDS];

/**
 * Base interface for WhatsApp tool request payloads
 */
export interface WaToolRequest<TData = any> {
    cmd: WaToolCommand;
    data?: TData;
    timeoutOverride?: number;
}

/**
 * Structured error object for tool responses.
 * `code` is stable (for frontend logic, analytics, translations).
 * `message` is human-readable and may change.
 */
export interface WaToolError {
    code: string;
    message: string;
    /** `rate_limited` only: seconds until a retry can succeed. */
    retryAfterSec?: number;
}

/**
 * Base interface for WhatsApp tool responses.
 *
 * Convention:
 * - success=true  → `data` contains the payload, `error` is absent
 * - success=false → `error` contains `{ code, message }`, `data` is absent
 *
 * Example success: { success: true, data: { user: { id: "u1" } } }
 * Example error:   { success: false, error: { code: "NOT_ON_WHATSAPP", message: "Phone not registered" } }
 */
export type WaToolResponse<TData = any> =
    | { success: true; data: TData }
    | { success: false; error: WaToolError };

/**
 * Helper to create a success response.
 *
 * The `T extends {success: false}` constraint refuses values that ALREADY look
 * like a failure envelope. Without it, `waToolSuccess({success:false,
 * error:...})` silently nests success-of-failure on the wire, which the MCP
 * backend then reads as a successful tool result with the failure shape as
 * payload (BE#96 root cause). Use `unwrapEngineResult` for engine results
 * that may carry the page-context error envelope.
 */
export const waToolSuccess = <T>(
    data: T extends { success: false } ? never : T,
): WaToolResponse<T> & { success: true } => ({ success: true, data: data as T });

/**
 * The engine error taxonomy: the only codes an engine reports. HTTP statuses are
 * a `/v1` concern and live in the backend (`engine-dispatch.ts`), not here.
 */
export const ENGINE_ERROR_CODES = [
    'not_connected',       // socket down / not synced yet
    'invalid_input',
    'not_found',           // a write targets a missing message/chat; a non-nullable read
    'not_a_group',
    'not_authorized',      // the account lacks the right (e.g. not a group admin)
    'not_on_whatsapp',     // permanent send failure
    'business_required',
    'account_restricted',  // permanent (ack 463 / restriction gate)
    'refused',             // WhatsApp rejected (stanza error, participant refusal)
    'rate_limited',        // router throttle and anti-spam backoff; carries retryAfterSec
    'ambiguous',           // may have happened (timeouts, thrown send errors) → the caller probes, never "failed"
    'unsupported_command', // not in the engine's manifest (router + extension default case)
    'engine_error',        // anything else (a handler bug)
] as const;
export type EngineErrorCode = (typeof ENGINE_ERROR_CODES)[number];

const ENGINE_ERROR_CODE_SET: ReadonlySet<string> = new Set(ENGINE_ERROR_CODES);

/**
 * The one engine-handler failure. An `EngineHandler` throws it; the pod's
 * MqttCommandRouter maps it to `{success:false, error:{code, message, retryAfterSec?}}`
 * and the extension's page handler site forwards `errorType: error.code`.
 */
export class EngineToolError extends Error {
    /** `failure` is the sanitized §1.4 record the throw site built (sanitizeFailure); runSendProtocol writes it. */
    constructor(readonly code: EngineErrorCode, message: string, readonly retryAfterSec?: number, readonly failure?: SendFailure) {
        super(message);
        this.name = 'EngineToolError';
    }
}

/** Helper to create an error response */
export const waToolError = (code: string, message: string): WaToolResponse<never> & { success: false } => ({
    success: false,
    error: { code, message },
});

/**
 * Wrap a page-context tool result for delivery to the gateway/MCP layer.
 *
 * The page-context handler chain (registerToolHandler in PageMessageService)
 * translates throws into `{success:false, error:<string>, errorStack, ...}`
 * envelopes. Many extension-context tool wrappers historically did
 * `return waToolSuccess(result)` blindly, which nests success-of-failure:
 *   { success: true, data: { success: false, error: '...', errorStack: '...' } }
 *
 * The MCP backend's `dispatchEngine` checks the OUTER success flag, so the
 * inner failure flows through as a successful tool result with stack traces
 * embedded — leaking chrome-extension URLs to the LLM (round-3 #45) or
 * masking real errors as data anomalies. Use this helper instead of
 * `waToolSuccess` whenever the wrapped value originated from a page-context
 * tool handler.
 */
/**
 * The success branch of the input — `Exclude` strips the page-handler
 * error envelope (`{success: false, ...}`) and `undefined` from the
 * inferred T so the wrapped `data` field's type is exactly the contract
 * Result, not the union including the failure shape. Without this the
 * caller's `T` widens to `X | WireErrorEnvelope | undefined` and the
 * thunk's typed return position rejects the unwrap.
 */
type EngineSuccess<T> = Exclude<T, { success: false } | undefined | null>;

export const unwrapEngineResult = <T>(result: T): WaToolResponse<EngineSuccess<T>> => {
    if (result === undefined || result === null) {
        return { success: false, error: { code: 'engine_error', message: 'No response from engine' } };
    }
    const r = result as any;
    if (r && typeof r === 'object' && r.success === false && (r.error || r.errorStack)) {
        const raw = typeof r.errorType === 'string' && r.errorType ? r.errorType : r?.error?.code;
        // Any code outside the taxonomy (an exception's class name, an old spelling) → engine_error.
        const code = typeof raw === 'string' && ENGINE_ERROR_CODE_SET.has(raw) ? raw : 'engine_error';
        const message = typeof r.error === 'string'
            ? r.error
            : (typeof r?.error?.message === 'string' ? r.error.message : 'engine error');
        const retryAfterSec = typeof r?.error?.retryAfterSec === 'number' ? r.error.retryAfterSec : undefined;
        return { success: false, error: retryAfterSec === undefined ? { code, message } : { code, message, retryAfterSec } };
    }
    return { success: true, data: r };
};

// ============================================================================
// Domain-Specific Request/Response Types
// ============================================================================

// ── Contact Domain Types ────────────────────────────────────────────────────

export interface GetProfilePicUrlRequest {
    contactId: string;
}

export interface GetProfilePicUrlResponse {
    url?: string;
}

// ── Chat Domain Types ───────────────────────────────────────────────────────

export interface GetChatParticipantsRequest {
    chatId?: string;
    limit?: number;
    offset?: number;
}

export interface GetChatParticipantsResponse {
    participants: any[];
}

export interface GetChatObjectByChatIdRequest {
    chatId: string;
}

export interface GetChatObjectByChatIdResponse {
    chat: any;
}


export interface SendSeenRequest {
    chatId: string;
}

export interface SendSeenResponse {
    // empty - success is conveyed by WaToolResponse wrapper
}

export interface OpenChatRequest {
    chatId?: string;
    phone?: string;
}

export interface OpenChatResponse {
    chatId?: string;
}

// ── Group Domain Types ──────────────────────────────────────────────────────

export interface CreateGroupRequest {
    groupName: string;
    participantChatIds: string[];
}

export interface UpdateGroupNameRequest {
    groupId: string;
    groupName: string;
}

export interface GetGroupDataRequest {
    groupId: string;
}

export interface UpdateGroupDescriptionRequest {
    groupId: string;
    settings: Record<string, any>;
}

export interface GroupParticipantRequest {
    groupId: string;
    participantChatId: string;
}

export interface SetGroupPictureRequest {
    groupId: string;
    fileDataUrl: string;
    fileName?: string;
    fileMimeType?: string;
}

// ── Message Domain Types ────────────────────────────────────────────────────

export interface GetMessagesRequest {
    chatId?: string;
    limit?: number;
    offset?: number;
    cutoffDate?: string;
    latestDate?: string;
    mode?: 'latest' | 'history';
    tail?: boolean;
}

export interface GetMessagesResponse {
    messages: any[];
}

// ── Unified Message Query Types ─────────────────────────────────────────────

/**
 * Unified parameters for message queries
 * Consolidates: getMessages, searchMessages, getChatMessages
 */
export interface QueryMessagesRequest {
    /** Chat ID - required for non-search queries */
    chatId?: string;

    /** Search mode - if provided, performs text search across messages */
    search?: {
        /** Search query (required for search mode) */
        query: string;
        /** Limit search to specific chat (optional) */
        chatId?: string;
    };

    /** Pagination options */
    pagination?: {
        /** Maximum number of results (-1 for all, default varies by mode) */
        limit?: number;
        /** Offset for pagination (default: 0) */
        offset?: number;
    };

    /** Fetch direction (default: 'history') */
    mode?: 'history' | 'latest';

    /** Date filtering options */
    dateFilter?: {
        /** ISO date string - get messages after this date */
        cutoffDate?: string;
        /** ISO date string - get messages before this date */
        latestDate?: string;
    };

    /** Response options */
    options?: {
        /** Remove large bodies from media messages (default: true) */
        slim?: boolean;
        /** Get messages from end of chat */
        tail?: boolean;
        /** Include hasMore flag for pagination (default: false) */
        includePaginationInfo?: boolean;
    };
}

/**
 * Unified result structure for message queries
 */
export interface QueryMessagesResponse {
    /** Messages list */
    messages: any[];
    /** Total messages in store */
    totalMessages: number;
    /** Paging info */
    paging: {
        /** Current offset */
        offset: number;
        /** Current limit */
        limit: number;
        /** More messages available in local store */
        hasMoreInStore: boolean;
        /** Can load more messages from phone */
        canLoadFromPhone: boolean;
        /** Whether loadFromPhoneIfNeeded was triggered */
        isLoadFromPhoneIfNeededTriggered: boolean;
        /** Number of messages added by loading from phone */
        totalMessagesAddedByLoadFromPhoneIfNeeded: number;
    };
}

export interface SearchMessagesRequest {
    query: string;
    chatId?: string;
    limit?: number;
}

export interface GetMessageMediaResponse {
    mimetype?: string;
    data?: string;
    filename?: string;
}

export interface GetMessageInfoRequest {
    waMessageKey: string;
}

export interface GetMessageInfoResponse {
    info: any;
}

export interface GetMessagesAckRequest {
    waMessageKeys: string[];
}

export interface GetMessagesAckResponse {
    ackData: any[];
}

export interface ReplyToMessageRequest {
    messageKey: string;
    content: string;
}

export interface ReplyToMessageResponse {
    messageId?: string;
}

export interface ReactToMessageRequest {
    messageKey: string;
    text: string;
}

export interface ReactToMessageResponse {
    // empty - success is conveyed by WaToolResponse wrapper
}

// ── Session Domain Types ────────────────────────────────────────────────────

export interface GetWaMeInfoRequest {
    // No params required
}

export interface GetWaMeInfoResponse {
    wid?: {
        _serialized: string;
        user: string;
        server: string;
    };
    name?: string;
    platform?: string;
    [key: string]: any;
}

export interface IsWhatsAppConnectedResponse {
    connected: boolean;
    state?: string;
    stream?: string;
    has_synced?: boolean;
}

// ── Validation Domain Types ─────────────────────────────────────────────────

export interface ValidateChatIdRequest {
    chatId: string;
    options?: {
        useForceUsync?: boolean;
    };
}

export interface ValidateChatIdResponse {
    valid: boolean;
    chatId?: string;
}

// ── Link Preview Domain Types ───────────────────────────────────────────────

export interface GetLinkPreviewRequest {
    link: string;
}

export interface GetLinkPreviewResponse {
    title?: string;
    description?: string;
    canonicalUrl?: string;
    matchedText?: string;
    thumbnail?: string;
}

// ── Diagnostics Domain Types ────────────────────────────────────────────────

export interface RunDiagnosticsRequest {
    engineId?: string;
    testIds?: string[];
    safetyLevels?: ('safe' | 'write' | 'dangerous')[];
    domains?: string[];
    inputOverrides?: Record<string, Record<string, any>>;
    timeoutMs?: number;
}

export interface RunDiagnosticsResponse {
    results: any[];
    summary: {
        total: number;
        passed: number;
        failed: number;
        skipped: number;
    };
}

// ── Load Messages From Phone Domain Types ──────────────────────────────────

export interface LoadMessagesFromPhoneRequest {
    chatId: string;
}

export interface LoadMessagesFromPhoneResponse {
    totalMessages: number;
    totalMessagesAddedByLoadFromPhoneIfNeeded: number;
    canLoadMore: boolean;
}
